import {
  Content,
  ContentAudience,
  ContentWorkingCopy,
  InlineComment,
  WorkingCopyKey,
} from '@amzn/awscat-pattern-library-service-model';
import PQueue from 'p-queue';

import { ContentWorkingCopyManager } from '../common/ContentWorkingCopyManager';
import { isContentValueEqual } from '../common/ContentEquality';
import { log } from '../loggers/Logger';
import { ContentAlreadyOpenError, NoContentOpenError } from './Errors';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';

/** Debounce delay (ms) before a staged edit is flushed to the working copy. */
export const AUTOSAVE_DELAY_MS = 3000;

/** Drives a Saving… / Saved / Error indicator in the UI. */
export type AutoSaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Options for {@link AutoSavingService.open}.
 *
 * `workingCopyKey` picks which working-copy slot (draft vs finalizing) the
 * auto-save target lives in. `isRestore` decides whether to rehydrate from
 * a previously auto-saved working copy under that key — typically set to
 * true after a tab crash or refresh.
 */
export interface AutoSavingOpenOptions {
  readonly workingCopyKey: WorkingCopyKey;
  /** When true, rehydrates from a previously auto-saved working copy (e.g. after a crashed tab). */
  readonly isRestore: boolean;
}

/**
 * Snapshot of everything edited locally but not yet committed to the
 * backend via `updateContent` / `updateFinalizingContent`.
 */
export interface PendingEdits {
  readonly contentId: string;
  readonly workingCopyKey: WorkingCopyKey;
  readonly content: Content | undefined;
  readonly audience: ContentAudience | undefined;
  /** Inline comments updated during editing, keyed by `commentId`. */
  readonly updatedComments: ReadonlyMap<string, InlineComment>;
  /** True when there are pending edits not yet saved to the working copy. */
  readonly isDirty: boolean;
  readonly saveState: AutoSaveState;
}

/** Events emitted by {@link AutoSavingService}. */
export type AutoSavingEvents = {
  /** Fired whenever `pendingEdits` changes. */
  changed: () => void;
  /** Fired when an async operation (flush/discard/auto-save) throws. */
  error: (err: Error) => void;
};

/**
 * Auto-saves in-progress edits for the single currently-opened content by
 * creating working copies via {@link ContentWorkingCopyManager}.
 *
 * Edits (content, audience, and inline-comment position shifts) are
 * staged synchronously so keystroke handlers stay cheap, then rolled up
 * into a single working-copy write on a debounced timer. Consumers can
 * also `flush()` immediately before committing to the backend, or
 * `discard()` to throw everything away and delete the working copy.
 *
 * {@link ContentEditorService} funnels its intermediate state here so it
 * doesn't need to know about the working-copy wire format. All async
 * operations run serially through a p-queue, so overlapping
 * open/flush/discard/auto-save calls are safe.
 */
export class AutoSavingService {
  private readonly _workingCopyMngr: ContentWorkingCopyManager;
  private readonly _autosaveDelayMs: number;
  private readonly _emitter = new TypedEmitter<AutoSavingEvents>();
  private readonly _queue = new PQueue({ concurrency: 1 });

  private _pending: PendingEdits | undefined = undefined;
  private _autosaveTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  constructor(workingCopyMngr: ContentWorkingCopyManager, autosaveDelayMs = AUTOSAVE_DELAY_MS) {
    this._workingCopyMngr = workingCopyMngr;
    this._autosaveDelayMs = autosaveDelayMs;
  }

  /** Snapshot of currently-pending edits. Undefined when nothing is open. */
  get pendingEdits(): PendingEdits | undefined {
    return this._pending;
  }

  /**
   * Begin tracking edits for `contentId`. Throws
   * {@link ContentAlreadyOpenError} if another content is already open.
   *
   * @throws {ContentAlreadyOpenError} when another content is already open.
   * @throws {ServiceCallError} when the backend call fails.
   */
  async open(contentId: string, options: AutoSavingOpenOptions): Promise<void> {
    await this._queue.add(() => this.doOpen(contentId, options));
  }

  /**
   * Release state without touching the working copy. Cancels the debounced
   * auto-save timer, drops queued tasks, then enqueues the actual teardown
   * so it serializes behind any in-flight task.
   */
  async close(): Promise<void> {
    this.cancelAutosave();
    this._queue.clear();
    await this._queue.add(() => this.doClose());
  }

  /**
   * Stage an edit to the content (and optionally audience). Marks dirty
   * and schedules a debounced auto-save. Throws {@link NoContentOpenError}
   * if nothing is open.
   *
   * Synchronous on purpose — auto-save scheduling happens off the hot
   * path so this stays cheap enough for keystroke handlers.
   */
  stageContentEdit(content: Content, audience: ContentAudience): void {
    const p = this.requireOpen('stageContentEdit');
    const nextAudience = audience ?? p.audience;
    if (isContentValueEqual(p.content, content) && p.audience === nextAudience) {
      return;
    }
    this._pending = {
      ...p,
      content,
      audience: nextAudience,
      isDirty: true,
    };
    this._emitter.emit('changed');
    this.scheduleAutosave();
  }

  /**
   * Stage inline comments whose offsets the editor reports as possibly
   * shifted due to content re-flow. Each comment is upserted by
   * `commentId`; callers hand in whatever subset their editor owns and
   * can call repeatedly for different subsets without clobbering unrelated
   * entries.
   *
   * The reconciliation against the server's authoritative list happens in
   * the commit path (see `InlineCommentManager.saveComments`), not here.
   */
  stageUpdatedComments(comments: readonly InlineComment[]): void {
    if (comments.length === 0) return;
    const p = this.requireOpen('stageUpdatedComments');
    const updatedComments = new Map(p.updatedComments);
    for (const c of comments) {
      updatedComments.set(c.commentId, c);
    }
    this._pending = {
      ...p,
      updatedComments,
      isDirty: true,
    };
    this._emitter.emit('changed');
    this.scheduleAutosave();
  }

  /**
   * Save pending edits to the working copy immediately, cancelling the
   * auto-save debounce. Returns the snapshot that was saved.
   *
   * @throws {SaveWorkingCopyError} when the underlying working-copy write fails.
   */
  async flush(): Promise<PendingEdits> {
    return this._queue.add(() => this.doFlush());
  }

  /**
   * Persist pending edits synchronously for a browser close/tab-hide handler,
   * where async work can't finish. Reconciles the remote on the next open.
   */
  flushImmediately(): void {
    const p = this._pending;
    if (!p || !p.isDirty || !p.content) return;

    // Stop the debounce so it can't fire a redundant queued autosave.
    this.cancelAutosave();

    // Fire the write outside the queue so its synchronous localStorage prefix
    // runs now; the local copy is what recovers the edit. The remote POST is
    // expected to die with the closing page — log it rather than fail loudly.
    void this.saveToWorkingCopy(p).catch((err) => {
      log.warn({ err, contentId: p.contentId }, 'Close-time working-copy upload did not complete');
    });
  }

  /**
   * Drop all pending edits and delete the local + remote working copies.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  async discard(): Promise<void> {
    await this._queue.add(() => this.doDiscard());
  }

  /** Subscribe to a service event. Returns an unsubscribe function. */
  on<K extends keyof AutoSavingEvents>(event: K, listener: AutoSavingEvents[K]): Unsubscribe {
    return this._emitter.on(event, listener);
  }

  // --------------------------------------------------------------------------
  // Internals
  // --------------------------------------------------------------------------

  /** @throws {ServiceCallError} when the backend call fails. */
  private async doOpen(contentId: string, options: AutoSavingOpenOptions): Promise<void> {
    if (this._pending) {
      throw new ContentAlreadyOpenError(this._pending.contentId, contentId);
    }
    let content: Content | undefined;
    let audience: ContentAudience | undefined;
    const updatedComments = new Map<string, InlineComment>();
    if (options.isRestore) {
      const mostRecent = await this._workingCopyMngr.getMostRecentWorkingCopy(
        contentId,
        options.workingCopyKey,
      );
      if (mostRecent?.workingCopy) {
        content = mostRecent.workingCopy.content;
        audience = mostRecent.workingCopy.contentAudience;
        const updated = mostRecent.workingCopy.updatedInlineComments ?? [];
        for (const c of updated) updatedComments.set(c.commentId, c);
      }
    }
    this._pending = {
      contentId,
      workingCopyKey: options.workingCopyKey,
      content,
      audience,
      updatedComments,
      isDirty: false,
      saveState: 'idle',
    };
    this._emitter.emit('changed');
  }

  private doClose(): void {
    this._pending = undefined;
    this._emitter.emit('changed');
  }

  /** @throws {SaveWorkingCopyError} when the working-copy write fails. */
  private async doFlush(): Promise<PendingEdits> {
    const p = this.requireOpen('flush');
    this.cancelAutosave();
    if (!p.isDirty || !p.content) {
      return p;
    }
    try {
      await this.saveToWorkingCopy(p);
    } catch (err) {
      // Keep isDirty=true so the next autosave/flush retries. Re-arm the
      // debounce so we don't silently stop saving if the user stops typing
      // after a failed flush. Rethrow so the caller sees the failure.
      // Spread from live pending (not the snapshot) so any edit staged
      // during the await isn't overwritten.
      if (this._pending) {
        this._pending = { ...this._pending, saveState: 'error' };
        this._emitter.emit('changed');
      }
      this._emitter.emit('error', err as Error);
      this.scheduleAutosave();
      throw err;
    }
    // The return value is the snapshot we actually persisted. The live
    // `_pending` may include edits staged during the await — see
    // `mergeAfterSave`.
    const saved: PendingEdits = { ...p, isDirty: false, saveState: 'saved' };
    const merged = this.mergeAfterSave(p);
    if (merged) {
      this._pending = merged;
      this._emitter.emit('changed');
    }
    return saved;
  }

  /** @throws {ServiceCallError} when the backend call fails. */
  private async doDiscard(): Promise<void> {
    const p = this._pending;
    if (!p) return;
    this.cancelAutosave();
    try {
      await this._workingCopyMngr.deleteWorkingCopies(p.contentId, p.workingCopyKey);
    } catch (err) {
      this._emitter.emit('error', err as Error);
      throw err;
    } finally {
      this._pending = undefined;
      this._emitter.emit('changed');
    }
  }

  private requireOpen(method: string): PendingEdits {
    if (!this._pending) throw new NoContentOpenError(method);
    return this._pending;
  }

  private cancelAutosave(): void {
    if (this._autosaveTimer !== undefined) {
      clearTimeout(this._autosaveTimer);
      this._autosaveTimer = undefined;
    }
  }

  private scheduleAutosave(): void {
    this.cancelAutosave();
    this._autosaveTimer = setTimeout(() => {
      // Route the auto-save through the queue so it can't race with an
      // in-flight flush/discard/open.
      void this._queue.add(() => this.autosave());
    }, this._autosaveDelayMs);
  }

  private async autosave(): Promise<void> {
    this._autosaveTimer = undefined;
    const p = this._pending;
    if (!p || !p.isDirty || !p.content) return;
    this.setSaveState('saving');
    try {
      await this.saveToWorkingCopy(p);
      // mergeAfterSave returns undefined if the content was closed (or
      // swapped) during the await, in which case we leave `_pending` alone.
      const merged = this.mergeAfterSave(p);
      if (merged) {
        this._pending = merged;
        this._emitter.emit('changed');
      }
    } catch (err) {
      this.setSaveState('error');
      this._emitter.emit('error', err as Error);
    }
  }

  /**
   * Merge the result of a successful save (`saved`) back into the live
   * `_pending`. New edits may have been staged synchronously during the
   * `saveToWorkingCopy` await — those replaced the staged-field references
   * (content / audience / updatedComments). If any reference
   * changed since the snapshot, isDirty must remain true so the next
   * autosave/flush picks them up, and saveState stays 'saving' so the UI
   * doesn't briefly report 'saved' while pending work is unsaved.
   *
   * Returns undefined when there's no live pending state to merge into
   * (e.g. content was closed during the save) — callers should leave
   * `_pending` alone in that case rather than resurrecting closed state.
   */
  private mergeAfterSave(saved: PendingEdits): PendingEdits | undefined {
    const live = this._pending;
    if (!live || live.contentId !== saved.contentId) return undefined;
    const nothingNewStaged =
      live.content === saved.content &&
      live.audience === saved.audience &&
      live.updatedComments === saved.updatedComments;
    return {
      ...live,
      isDirty: nothingNewStaged ? false : true,
      saveState: nothingNewStaged ? 'saved' : 'saving',
    };
  }

  private setSaveState(state: AutoSaveState): void {
    if (!this._pending) return;
    this._pending = { ...this._pending, saveState: state };
    this._emitter.emit('changed');
  }

  /** @throws {SaveWorkingCopyError} when the backend write fails. */
  private async saveToWorkingCopy(p: PendingEdits): Promise<void> {
    if (!p.content) return;
    const workingCopy = ContentWorkingCopy.create(p.content, p.audience as ContentAudience);
    if (p.updatedComments.size > 0) {
      workingCopy.updatedInlineComments = Array.from(p.updatedComments.values());
    }
    await this._workingCopyMngr.createContentWorkingCopy(
      p.contentId,
      p.workingCopyKey,
      workingCopy,
      // We're supplying updated comments directly; the manager's own
      // LocalStorage lookup would otherwise duplicate them.
      false,
    );
  }
}
